/**
 * Derivación de las Capa_Mapa del Mapa_Operacional desde los JSONs
 * reales de Antofagasta (`frontend/public/data/*.json`).
 *
 * Implementa la parte de **datos** del Mapa_Operacional (R1): convierte
 * `dmas.json`, `gateways.json` y `alarmas.json` en las Capa_Mapa
 * geográficas que MapLibre (y Supercluster, para clustering) consumen.
 * Cada Capa_Mapa se materializa como una `FeatureCollection` de GeoJSON
 * con geometría `Point`, de modo que sea directamente renderizable por
 * MapLibre GL y troceable por Supercluster (`index.load(features)`).
 *
 * Principios de diseño:
 *   - **Funciones puras**: reciben los datos como parámetro (no hacen
 *     `fetch` ni tocan estado global), de modo que sean testeables en
 *     Node/Vitest (Properties 2 y 4). El fetch real (`loadRealData`) se
 *     mantiene fuera de este módulo.
 *   - **No duplicación (R17)**: las Features referencian las entidades
 *     por `entidad_ref` y reutilizan los `node_id` deterministas y la
 *     misma derivación sintética por DMA del Grafo_Entidades
 *     (`./graph`), sin redefinir el esquema de la fundación.
 *   - **Exclusión exacta (R1.8)**: una entidad sin coordenadas
 *     geográficas válidas se **excluye** de las Capa_Mapa geográficas y
 *     se registra como "dato incompleto" mediante el hook de
 *     Observabilidad, sin interrumpir el resto del renderizado.
 *
 * La terminología (Mapa_Operacional, Capa_Mapa, DMA, Gateway, Valvula,
 * PRV, Cuadrilla, Presion, Anomalia, Heatmap_NRW) corresponde al
 * Glosario de `requirements.md`.
 *
 * _Requirements: 1.1, 1.7, 1.8_
 */

import type { Feature, FeatureCollection, Point } from "geojson";

import type { CapaMapa, Coordenada, EntidadRef, Id, TipoEntidad } from "../api/contracts";
import type { RealAlarma, RealDma, RealGateway } from "../api/realData";
import {
  GRAPH_CONFIG_DEFAULT,
  nodeIdFor,
  type GraphConfig,
  type GraphData,
} from "./graph";

// ---------------------------------------------------------------------------
// Tipos de las Capa_Mapa (GeoJSON consumible por MapLibre/Supercluster)
// ---------------------------------------------------------------------------

/**
 * Propiedades de una Feature de una Capa_Mapa. Identifican la entidad
 * subyacente por referencia (R17) y exponen su estado vigente para la
 * selección (R1.6) y el clustering (R1.3).
 */
export interface FeaturePropsMapa {
  /** Capa_Mapa a la que pertenece la Feature. */
  capa: CapaMapa;
  /** Tipo de entidad de la fundación o de la Capa. */
  tipo: TipoEntidad;
  /** Identificador de la entidad en su almacén de origen. */
  id: Id;
  /** `node_id` determinista en el Grafo_Entidades (consistencia R1.6). */
  node_id: Id;
  /** Referencia lógica a la entidad subyacente (no la duplica, R17). */
  entidad_ref: EntidadRef;
  /** Estado vigente legible (ej. "abierta", "fuga"). */
  estado?: string;
  /** Atributos de proyección adicionales por tipo de capa. */
  [extra: string]: unknown;
}

/** Una Feature geográfica (Point) de una Capa_Mapa. */
export type FeatureMapa = Feature<Point, FeaturePropsMapa>;

/** Una Capa_Mapa materializada como FeatureCollection de GeoJSON. */
export type ColeccionCapa = FeatureCollection<Point, FeaturePropsMapa>;

/** Motivo por el que una entidad se excluye de las Capa_Mapa (R1.8). */
export type MotivoExclusion =
  | "coordenadas_invalidas"
  | "sin_dma_asociada"
  | "indice_dma_fuera_de_rango";

/**
 * Registro de "dato incompleto": entidad excluida de las Capa_Mapa
 * geográficas por carecer de coordenadas válidas o de una DMA que la
 * georreferencie (R1.8). Se entrega a la Observabilidad vía hook.
 */
export interface ExclusionDatoIncompleto {
  capa: CapaMapa;
  tipo: TipoEntidad;
  id: Id;
  motivo: MotivoExclusion;
  detalle: string;
}

/**
 * Hook a la Observabilidad: se invoca una vez por cada entidad excluida
 * como dato incompleto (R1.8). La integración concreta con el
 * subsistema de Observabilidad se cablea en una tarea posterior; aquí se
 * deja el punto de extensión y, además, se devuelven las exclusiones en
 * el resultado para que el llamador las registre.
 */
export type HookObservabilidad = (exclusion: ExclusionDatoIncompleto) => void;

/**
 * Resultado de derivar las Capa_Mapa: el conjunto de capas geográficas
 * y la lista de entidades excluidas como dato incompleto (R1.1, R1.8).
 */
export interface ResultadoCapasMapa {
  /** Capa_Mapa geográficas indexadas por su identificador (R1.1). */
  capas: Record<CapaMapaGeografica, ColeccionCapa>;
  /** Entidades excluidas por dato incompleto, en orden de derivación (R1.8). */
  exclusiones: ExclusionDatoIncompleto[];
}

/** Subconjunto de Capa_Mapa que este módulo deriva desde los JSONs. */
export type CapaMapaGeografica =
  | "dmas"
  | "gateways"
  | "valvulas"
  | "prvs"
  | "cuadrillas"
  | "presion"
  | "anomalias"
  | "heatmap_nrw"
  | "fugas_historicas";

/** Capa_Mapa geográficas que este módulo produce, en orden de capa. */
export const CAPAS_GEOGRAFICAS: CapaMapaGeografica[] = [
  "dmas",
  "gateways",
  "valvulas",
  "prvs",
  "cuadrillas",
  "presion",
  "anomalias",
  "heatmap_nrw",
  "fugas_historicas",
];

// ---------------------------------------------------------------------------
// Validación de coordenadas (R1.7, R1.8)
// ---------------------------------------------------------------------------

/**
 * Determina si un par (lon, lat) es una coordenada geográfica válida en
 * el sistema de la fundación (WGS84): ambos finitos, longitud en
 * [-180, 180] y latitud en [-90, 90] (R1.7). Excluye `NaN`, `Infinity`,
 * `null`/`undefined` y el origen nulo (0, 0), que en este dominio
 * (Antofagasta) indica coordenada ausente.
 */
export function esCoordenadaValida(lon: unknown, lat: unknown): lon is number {
  if (typeof lon !== "number" || typeof lat !== "number") return false;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return false;
  if (lon === 0 && lat === 0) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Helpers de construcción
// ---------------------------------------------------------------------------

/**
 * Modelo de línea de costa de Antofagasta como **polilínea** (no una
 * recta). La ciudad es una franja urbana estrecha entre el océano
 * Pacífico (al **oeste**) y el desierto (al **este**); la costa real se
 * curva —sobre todo en la bahía del puerto/centro— por lo que una sola
 * recta dejaba puntos en el agua. Aquí la costa se define por vértices
 * (lat, lon) ordenados de norte a sur, trazados de forma conservadora
 * justo en la orilla; la longitud de la costa para una latitud dada se
 * obtiene por interpolación lineal entre vértices. Cualquier punto en
 * tierra debe quedar al **este** de esta línea (longitud mayor), de modo
 * que nada se siembre sobre el mar (R1.7).
 *
 * Los vértices se eligen ligeramente tierra adentro de la orilla real
 * para absorber el error del modelo; el conjunto cubre todo el rango de
 * latitudes de las DMAs (de La Chimba al norte a Playa Blanca al sur).
 */
const COSTA_VERTICES: ReadonlyArray<{ lat: number; lon: number }> = [
  { lat: -23.50, lon: -70.385 },
  { lat: -23.55, lon: -70.392 },
  { lat: -23.58, lon: -70.396 },
  { lat: -23.60, lon: -70.398 },
  { lat: -23.63, lon: -70.400 },
  { lat: -23.65, lon: -70.401 },
  { lat: -23.665, lon: -70.402 },
  { lat: -23.685, lon: -70.409 },
  { lat: -23.71, lon: -70.414 },
  { lat: -23.74, lon: -70.424 },
  { lat: -23.77, lon: -70.430 },
];
/** Franja de seguridad (≈250 m) tierra adentro desde la línea de costa. */
const COSTA_BUFFER = 0.0025;
/** Ancho máximo de la franja urbana hacia el interior (≈4,5 km). */
const CIUDAD_ANCHO = 0.045;

/**
 * Longitud de la costa (borde con el mar) para una latitud dada,
 * interpolada linealmente sobre la polilínea {@link COSTA_VERTICES}.
 * Fuera del rango de vértices, se extrapola con el vértice extremo.
 */
function lonCosta(lat: number): number {
  const v = COSTA_VERTICES;
  // Los vértices van de norte (lat mayor) a sur (lat menor).
  if (lat >= v[0].lat) return v[0].lon;
  if (lat <= v[v.length - 1].lat) return v[v.length - 1].lon;
  for (let i = 0; i < v.length - 1; i++) {
    const a = v[i];
    const b = v[i + 1];
    if (lat <= a.lat && lat >= b.lat) {
      const t = (lat - a.lat) / (b.lat - a.lat);
      return a.lon + t * (b.lon - a.lon);
    }
  }
  return v[v.length - 1].lon;
}

/** ¿La coordenada base cae en la región urbana de Antofagasta? */
function enRegionAntofagasta(base: Coordenada): boolean {
  return base.lon >= -71 && base.lon <= -70 && base.lat >= -24.2 && base.lat <= -23.2;
}

/**
 * Empuja un punto a **tierra** si cayó sobre el mar: si su longitud es
 * menor que la de la costa (más al oeste), lo reubica al este de la
 * orilla. Usa una reflexión hacia el interior para conservar el aspecto
 * disperso (en vez de apilar los puntos sobre la línea), acotada al
 * ancho de la franja urbana. Solo se aplica en la región de
 * Antofagasta; fuera de ella el punto se deja intacto.
 */
function aTierra(coord: Coordenada): Coordenada {
  if (!enRegionAntofagasta(coord)) return coord;
  const min = lonCosta(coord.lat) + COSTA_BUFFER;
  if (coord.lon >= min) return coord;
  const max = lonCosta(coord.lat) + CIUDAD_ANCHO;
  let lon = min + (min - coord.lon); // reflexión hacia el interior
  if (lon > max) lon = max;
  if (lon < min) lon = min; // garantía dura: nunca al oeste de la orilla
  return { lon, lat: coord.lat };
}

/**
 * Hash FNV-1a de 32 bits sobre una cadena. Determinista y bien
 * distribuido; se usa para sembrar posiciones reproducibles
 * (mismo identificador ⇒ misma posición).
 */
function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Fracción determinista en [0, 1) derivada del hash de una semilla. */
function fraccion(seed: string): number {
  return hash32(seed) / 0x100000000;
}

/**
 * Dispersión determinista de aspecto aleatorio alrededor de una
 * coordenada base. Usa el hash del identificador de la entidad para
 * derivar un ángulo y un radio (distribución uniforme en disco), de modo
 * que las entidades de una misma DMA queden repartidas de forma natural
 * —no en una rejilla— y reproducible. Se aplica un **sesgo hacia el este**
 * (tierra adentro) en la componente de longitud para alejar los puntos
 * del borde costero, y el resultado se empuja a tierra con
 * {@link aTierra} para que nada caiga sobre el mar (R1.7).
 */
function scatterCoord(base: Coordenada, seed: string, radio = 0.014): Coordenada {
  const ang = fraccion(`${seed}:ang`) * Math.PI * 2;
  const r = Math.sqrt(fraccion(`${seed}:rad`)) * radio;
  // Sesgo hacia el interior (este): la mitad del radio se suma siempre a
  // la longitud, de modo que la nube de puntos se aleja de la orilla.
  const sesgoEste = radio * 0.5;
  return aTierra({
    lon: base.lon + Math.cos(ang) * r + sesgoEste,
    lat: base.lat + Math.sin(ang) * r,
  });
}

/** Construye una Feature de GeoJSON `Point` para una Capa_Mapa. */
function featurePunto(
  capa: CapaMapa,
  ref: EntidadRef,
  coord: Coordenada,
  extra: Record<string, unknown> = {},
): FeatureMapa {
  return {
    type: "Feature",
    geometry: { type: "Point", coordinates: [coord.lon, coord.lat] },
    properties: {
      capa,
      tipo: ref.tipo,
      id: ref.id,
      node_id: nodeIdFor(ref),
      entidad_ref: ref,
      ...extra,
    },
  };
}

/** Envuelve una lista de Features en una FeatureCollection. */
function coleccion(features: FeatureMapa[]): ColeccionCapa {
  return { type: "FeatureCollection", features };
}

/** Tipos de alarma de `alarmas.json` proyectados como Anomalias. */
const TIPOS_ALARMA: Array<{ campo: keyof RealAlarma; etiqueta: string }> = [
  { campo: "alarm_leakage", etiqueta: "fuga" },
  { campo: "alarm_burst", etiqueta: "reventon" },
  { campo: "alarm_backflow", etiqueta: "backflow" },
  { campo: "alarm_dry", etiqueta: "tuberia_seca" },
  { campo: "alarm_manipulation", etiqueta: "manipulacion" },
  { campo: "alarm_battery", etiqueta: "bateria" },
  { campo: "alarm_permanent", etiqueta: "permanente" },
];

/** Alarmas que constituyen una fuga histórica para la capa homónima. */
const TIPOS_FUGA: Array<keyof RealAlarma> = ["alarm_leakage", "alarm_burst"];

// ---------------------------------------------------------------------------
// Estado de acumulación de exclusiones (R1.8)
// ---------------------------------------------------------------------------

/** Acumulador interno de exclusiones que invoca el hook de Observabilidad. */
class RegistroExclusiones {
  readonly exclusiones: ExclusionDatoIncompleto[] = [];
  constructor(private readonly hook?: HookObservabilidad) {}

  registrar(e: ExclusionDatoIncompleto): void {
    this.exclusiones.push(e);
    this.hook?.(e);
  }
}

// ---------------------------------------------------------------------------
// Derivación por Capa_Mapa (funciones puras)
// ---------------------------------------------------------------------------

/**
 * Capa de DMAs: una Feature por DMA con coordenada válida. Las DMAs sin
 * coordenadas válidas se excluyen y se registran como dato incompleto
 * (R1.8). La DMA es la entidad raíz de la jerarquía del Grafo (R11.2).
 */
export function deriveCapaDmas(dmas: RealDma[], reg: RegistroExclusiones): ColeccionCapa {
  const features: FeatureMapa[] = [];
  for (const dma of dmas) {
    const ref: EntidadRef = { tipo: "DMA", id: dma.codigo };
    if (!esCoordenadaValida(dma.lon, dma.lat)) {
      reg.registrar({
        capa: "dmas",
        tipo: "DMA",
        id: dma.codigo,
        motivo: "coordenadas_invalidas",
        detalle: `DMA ${dma.codigo} sin coordenadas geográficas válidas (lon=${dma.lon}, lat=${dma.lat}).`,
      });
      continue;
    }
    features.push(
      featurePunto("dmas", ref, { lon: dma.lon, lat: dma.lat }, {
        estado: "activa",
        nombre: dma.nombre,
        clientes: dma.clientes,
        nrw_pct: dma.nrw_pct,
      }),
    );
  }
  return coleccion(features);
}

/**
 * Capa de Heatmap_NRW: intensidad espacial del Agua No Facturada por
 * DMA. Cada Feature lleva un `weight` (NRW %) para alimentar un
 * `HeatmapLayer`. Reutiliza la coordenada de la DMA (R1.8 para las DMAs
 * sin coordenadas válidas, ya registradas por {@link deriveCapaDmas}).
 */
export function deriveCapaHeatmapNrw(dmas: RealDma[]): ColeccionCapa {
  const features: FeatureMapa[] = [];
  for (const dma of dmas) {
    if (!esCoordenadaValida(dma.lon, dma.lat)) continue;
    const ref: EntidadRef = { tipo: "DMA", id: dma.codigo };
    features.push(
      featurePunto("heatmap_nrw", ref, { lon: dma.lon, lat: dma.lat }, {
        capa: "heatmap_nrw",
        weight: dma.nrw_pct,
        nombre: dma.nombre,
      }),
    );
  }
  return coleccion(features);
}

/**
 * Capas de entidades sintéticas por DMA (Valvulas, PRVs, Cuadrillas y
 * puntos de Presion). Se derivan desde cada DMA con coordenada válida y
 * se **dispersan** de forma determinista (hash del identificador, vía
 * {@link scatterCoord}) en un radio amplio alrededor de la DMA, en vez
 * de una rejilla fija, para que el mapa luzca natural y repartido. Todo
 * punto se empuja a tierra para que nada caiga sobre el mar (R1.7). El
 * identificador de la entidad es el mismo que en el Grafo_Entidades
 * (`./graph`), por lo que la selección en el mapa sigue siendo
 * consistente (R1.6, que se resuelve por `node_id`, no por coordenada).
 * Devuelve las cuatro colecciones a la vez para recorrer las DMAs una
 * sola vez.
 */
export function deriveCapasSinteticas(
  dmas: RealDma[],
  config: GraphConfig,
): { valvulas: ColeccionCapa; prvs: ColeccionCapa; cuadrillas: ColeccionCapa; presion: ColeccionCapa } {
  const valvulas: FeatureMapa[] = [];
  const prvs: FeatureMapa[] = [];
  const cuadrillas: FeatureMapa[] = [];
  const presion: FeatureMapa[] = [];

  for (const dma of dmas) {
    if (!esCoordenadaValida(dma.lon, dma.lat)) continue; // DMA ya registrada como dato incompleto.
    const base: Coordenada = { lon: dma.lon, lat: dma.lat };

    for (let i = 0; i < config.valvulasPorDma; i++) {
      const id = `${dma.codigo}-V${i + 1}`;
      const ref: EntidadRef = { tipo: "Valvula", id };
      valvulas.push(featurePunto("valvulas", ref, scatterCoord(base, id), { estado: "abierta", dma_ref: dma.codigo }));
    }
    for (let i = 0; i < config.prvsPorDma; i++) {
      const id = `${dma.codigo}-PRV${i + 1}`;
      const ref: EntidadRef = { tipo: "PRV", id };
      prvs.push(featurePunto("prvs", ref, scatterCoord(base, id), { estado: "regulando", setpoint: 35, dma_ref: dma.codigo }));
    }
    for (let i = 0; i < config.cuadrillasPorDma; i++) {
      const id = `${dma.codigo}-C${i + 1}`;
      const ref: EntidadRef = { tipo: "Cuadrilla", id };
      cuadrillas.push(featurePunto("cuadrillas", ref, scatterCoord(base, id), { estado: "disponible", dma_ref: dma.codigo }));
    }
    for (let i = 0; i < config.puntosPresionPorDma; i++) {
      const id = `${dma.codigo}-P${i + 1}`;
      const ref: EntidadRef = { tipo: "Presion", id };
      presion.push(featurePunto("presion", ref, scatterCoord(base, id), { unidad: "mca", dma_ref: dma.codigo }));
    }
  }

  return {
    valvulas: coleccion(valvulas),
    prvs: coleccion(prvs),
    cuadrillas: coleccion(cuadrillas),
    presion: coleccion(presion),
  };
}

/**
 * Caja envolvente (bounding box) de la huella urbana cubierta por las
 * DMAs válidas, con un margen para que las estaciones base puedan
 * sembrarse también en la periferia de la red.
 */
interface CajaGeografica {
  oeste: number;
  sur: number;
  este: number;
  norte: number;
}

function cajaDmas(dmasValidas: RealDma[]): CajaGeografica {
  const lons = dmasValidas.map((d) => d.lon);
  const lats = dmasValidas.map((d) => d.lat);
  let oeste = Math.min(...lons);
  let este = Math.max(...lons);
  let sur = Math.min(...lats);
  let norte = Math.max(...lats);
  // Margen mínimo para cajas degeneradas (una sola DMA) y para sembrar
  // gateways en el borde de la cobertura.
  const margenLon = Math.max((este - oeste) * 0.15, 0.02);
  const margenLat = Math.max((norte - sur) * 0.15, 0.02);
  oeste -= margenLon;
  este += margenLon;
  sur -= margenLat;
  norte += margenLat;
  return { oeste, sur, este, norte };
}

/**
 * Capa de Gateways: estaciones base LoRaWAN. `gateways.json` no trae
 * coordenadas propias, por lo que cada Gateway se **dispersa** de forma
 * determinista sobre la huella urbana cubierta por las DMAs (caja
 * envolvente con margen), usando un hash de su `gateway_id` para
 * sembrar lon/lat. Así las estaciones quedan repartidas por toda la
 * ciudad —como una red LoRaWAN real— en lugar de apilarse sobre las
 * DMAs. Todo punto se empuja a tierra ({@link aTierra}) para que nada
 * caiga sobre el mar. La asignación es reproducible (mismo id ⇒ misma
 * posición). Un Gateway que no puede georreferenciarse —porque no hay
 * DMAs con coordenadas válidas— se excluye y se registra como dato
 * incompleto (R1.8).
 */
export function deriveCapaGateways(
  gateways: RealGateway[],
  dmas: RealDma[],
  reg: RegistroExclusiones,
): ColeccionCapa {
  const dmasValidas = dmas.filter((d) => esCoordenadaValida(d.lon, d.lat));
  const features: FeatureMapa[] = [];
  const caja = dmasValidas.length > 0 ? cajaDmas(dmasValidas) : null;

  gateways.forEach((gw) => {
    const ref: EntidadRef = { tipo: "Gateway", id: gw.gateway_id };
    if (!caja) {
      reg.registrar({
        capa: "gateways",
        tipo: "Gateway",
        id: gw.gateway_id,
        motivo: "sin_dma_asociada",
        detalle: `Gateway ${gw.gateway_id} sin coordenadas propias y sin DMA georreferenciable que lo ubique.`,
      });
      return;
    }
    // Siembra determinista dentro de la caja urbana (dos ejes independientes),
    // empujada a tierra para no caer sobre el mar.
    const fx = fraccion(gw.gateway_id);
    const fy = fraccion(`${gw.gateway_id}:lat`);
    const coord: Coordenada = aTierra({
      lon: caja.oeste + fx * (caja.este - caja.oeste),
      lat: caja.sur + fy * (caja.norte - caja.sur),
    });
    // DMA de cobertura nominal: la válida más cercana a la siembra.
    const dma = dmasValidas.reduce((mejor, d) => {
      const dActual = (d.lon - coord.lon) ** 2 + (d.lat - coord.lat) ** 2;
      const dMejor = (mejor.lon - coord.lon) ** 2 + (mejor.lat - coord.lat) ** 2;
      return dActual < dMejor ? d : mejor;
    }, dmasValidas[0]);
    features.push(
      featurePunto("gateways", ref, coord, {
        estado: "online",
        medidores: gw.medidores,
        avg_sf: gw.avg_sf,
        avg_rssi: gw.avg_rssi,
        dma_ref: dma.codigo,
      }),
    );
  });

  return coleccion(features);
}

/**
 * Capas de Anomalias y de fugas históricas, derivadas de `alarmas.json`.
 * Cada flag de alarma activo genera una Anomalia georreferenciada sobre
 * la DMA del medidor (índice `dma_idx`). Las alarmas con `dma_idx` fuera
 * de rango, o cuya DMA carece de coordenadas válidas, se excluyen y se
 * registran como dato incompleto (R1.8). Las fugas (leak/burst) se
 * proyectan además en la capa `fugas_historicas`.
 */
export function deriveCapasAlarmas(
  alarmas: RealAlarma[],
  dmas: RealDma[],
  reg: RegistroExclusiones,
): { anomalias: ColeccionCapa; fugas_historicas: ColeccionCapa } {
  const anomalias: FeatureMapa[] = [];
  const fugas: FeatureMapa[] = [];

  alarmas.forEach((al) => {
    const indiceValido = Number.isInteger(al.dma_idx) && al.dma_idx >= 0 && al.dma_idx < dmas.length;
    if (!indiceValido) {
      reg.registrar({
        capa: "anomalias",
        tipo: "Anomalia",
        id: al.device,
        motivo: "indice_dma_fuera_de_rango",
        detalle: `Alarma del medidor ${al.device} con dma_idx=${al.dma_idx} fuera del rango [0, ${dmas.length}).`,
      });
      return;
    }
    const dma = dmas[al.dma_idx];
    if (!esCoordenadaValida(dma.lon, dma.lat)) {
      reg.registrar({
        capa: "anomalias",
        tipo: "Anomalia",
        id: al.device,
        motivo: "coordenadas_invalidas",
        detalle: `Alarma del medidor ${al.device} asociada a la DMA ${dma.codigo} sin coordenadas válidas.`,
      });
      return;
    }

    const base: Coordenada = { lon: dma.lon, lat: dma.lat };
    let activadas = 0;
    TIPOS_ALARMA.forEach(({ campo, etiqueta }) => {
      if (al[campo] !== true) return;
      const ref: EntidadRef = { tipo: "Anomalia", id: `${al.device}:${etiqueta}` };
      const coord = scatterCoord(base, `${al.device}:${etiqueta}`);
      anomalias.push(
        featurePunto("anomalias", ref, coord, {
          estado: etiqueta,
          medidor_ref: al.device,
          dma_ref: dma.codigo,
          ts: al.root_timestamp,
        }),
      );
      activadas++;
      if (TIPOS_FUGA.includes(campo)) {
        fugas.push(
          featurePunto("fugas_historicas", ref, coord, {
            estado: etiqueta,
            medidor_ref: al.device,
            dma_ref: dma.codigo,
            ts: al.root_timestamp,
          }),
        );
      }
    });
    void activadas;
  });

  return { anomalias: coleccion(anomalias), fugas_historicas: coleccion(fugas) };
}

// ---------------------------------------------------------------------------
// Derivación completa
// ---------------------------------------------------------------------------

/**
 * Deriva **todas** las Capa_Mapa geográficas del Mapa_Operacional desde
 * los datos crudos de `dmas.json`, `gateways.json` y `alarmas.json`
 * (R1.1). Función **pura**: mismos datos → mismas capas y mismas
 * exclusiones, en el mismo orden; no hace `fetch` ni accede a estado
 * global. Las entidades sin coordenadas geográficas válidas se excluyen
 * y se registran como dato incompleto vía el hook de Observabilidad y en
 * el campo `exclusiones` del resultado (R1.8).
 *
 * @param data    Datos crudos (DMAs obligatorias; gateways/alarmas opcionales).
 * @param config  Configuración de derivación sintética por DMA (defecto: la del Grafo).
 * @param onExclusion  Hook a la Observabilidad invocado por cada exclusión (R1.8).
 */
export function deriveMapLayers(
  data: GraphData,
  config: GraphConfig = GRAPH_CONFIG_DEFAULT,
  onExclusion?: HookObservabilidad,
): ResultadoCapasMapa {
  const dmas = data.dmas ?? [];
  const gateways = data.gateways ?? [];
  const alarmas = data.alarmas ?? [];

  const reg = new RegistroExclusiones(onExclusion);

  const dmasCapa = deriveCapaDmas(dmas, reg);
  const heatmap = deriveCapaHeatmapNrw(dmas);
  const sinteticas = deriveCapasSinteticas(dmas, config);
  const gatewaysCapa = deriveCapaGateways(gateways, dmas, reg);
  const { anomalias, fugas_historicas } = deriveCapasAlarmas(alarmas, dmas, reg);

  return {
    capas: {
      dmas: dmasCapa,
      gateways: gatewaysCapa,
      valvulas: sinteticas.valvulas,
      prvs: sinteticas.prvs,
      cuadrillas: sinteticas.cuadrillas,
      presion: sinteticas.presion,
      anomalias,
      heatmap_nrw: heatmap,
      fugas_historicas,
    },
    exclusiones: reg.exclusiones,
  };
}
